import { getDecimalResolution } from "@onshopfront/components";
import { getOption, subscribeToOption } from "../Platform/Options/Options";

const formatterMap = new Map();
let currencySymbol = "$";
let currencyBefore = true;
let currencyDecimalPlaces = 2;

/**
 * Get a number formatter
 */
export function getFormatter(type: "currency" | "percentage", decimalPlaces?: number): Intl.NumberFormat {
    const key = `${type}${typeof decimalPlaces === "undefined" ? "" : decimalPlaces}`;

    if(formatterMap.has(key)) {
        return formatterMap.get(key);
    }

    const options: Intl.NumberFormatOptions = {
        useGrouping          : true,
        maximumFractionDigits: decimalPlaces,
        minimumFractionDigits: decimalPlaces,
    };

    switch(type) {
        case "currency":
            options.style = "currency";
            options.currencyDisplay = "symbol";
            options.currency = getOption("currency_code");
            break;
        case "percentage":
            options.style = "percent";
            break;
    }

    let formatter;

    try {
        formatter = new Intl.NumberFormat(getOption("number_locale"), options);
    } catch {
        if(type === "currency") {
            options.currency = "AUD";
        }

        formatter = new Intl.NumberFormat("en-AU", options);
    }

    formatterMap.set(key, formatter);

    return formatter;
}

/**
 * Configure the currency settings (symbol and position)
 */
function configureCurrency(): void {
    const formatter = getFormatter("currency");

    if(typeof formatter.formatToParts === "undefined") {
        // Safari on iOS <13
        console.log("Only supporting AUD currency due to formatToParts not being implemented");
        currencySymbol = "$";
        currencyBefore = true;
        currencyDecimalPlaces = 2;

        return;
    }

    const parts = formatter.formatToParts(1);

    currencyDecimalPlaces = formatter.resolvedOptions().maximumFractionDigits ?? 2;

    let beforeNumbers = true;

    for(let i = 0, l = parts.length; i < l; i++) {
        if(parts[i].type === "currency") {
            currencySymbol = parts[i].value;
            currencyBefore = beforeNumbers;
            break;
        } else if(parts[i].type === "integer") {
            beforeNumbers = false;
        }
    }
}

configureCurrency();

subscribeToOption("currency_code", (_, oldValue, newValue) => {
    if(oldValue !== newValue) {
        formatterMap.clear();
        configureCurrency();
    }
});

subscribeToOption("number_locale", (_, oldValue, newValue) => {
    if(oldValue !== newValue) {
        formatterMap.clear();
        configureCurrency();
    }
});

/**
 * Get the currency symbol for the current currency
 */
export function getCurrencySymbol(): string {
    return currencySymbol;
}

/**
 * Get whether the currency symbol should be before the value
 */
export function isCurrencySymbolBeforeNumbers(): boolean {
    return currencyBefore;
}

/**
 * Get the number of decimal places this currency uses
 */
export function getCurrencyDecimalPlaces(): number {
    return currencyDecimalPlaces;
}

/**
 * Set the number of decimal places to round the currency to.
 * NOTE: This should only be used in testing, because of this, I'm marking as deprecated
 * @deprecated
 */
export function setCurrencyDecimalPlaces(places: number): void {
    currencyDecimalPlaces = places;
}

/**
 * Parses a currency string
 */
export function parseCurrency(amount: string): number {
    amount = amount.replace(",", "."); // To change other formats (such as euro)

    const split = amount.split(".");

    if(split.length === 3) {
        amount = `${split[0]},${split[1]}.${split[2]}`;
    }

    return Number(amount.replace(/[^0-9.-]+/g, ""));
}

/**
 * Formats a number into a currency
 */
export function currency(
    amount: number,
    blankIfZero = false,
    decimalPlaces?: number
): string {
    if(!amount && blankIfZero || isNaN(amount) && blankIfZero) {
        return "";
    } else if(isNaN(amount)) {
        amount = 0;
    }

    return getFormatter("currency", decimalPlaces).format(amount);
}

/**
 * Formats a number into a percentage
 */
export function percentage(
    amount: number,
    decimalPlaces = 2,
    blankIfZero = false
): string {
    if(!amount && blankIfZero || isNaN(amount) && blankIfZero) {
        return "";
    } else if(isNaN(amount)) {
        amount = 0;
    }

    return getFormatter("percentage", decimalPlaces).format(amount / 100);
}

/**
 * Display a number with the specified number of decimal places (does not round)
 */
export function toDecimalPlaces(
    amount: number | string,
    decimalPlaces = currencyDecimalPlaces
): string {
    if(typeof amount === "string") {
        amount = parseFloat(amount);
    }

    if(isNaN(amount)) {
        amount = 0;
    }

    return amount.toFixed(decimalPlaces);
}

/**
 * Scale a number to the provided number of decimal places
 */
export function scaleToDecimalPlaces(
    amount: number,
    decimalPlaces = currencyDecimalPlaces
): number {
    if(decimalPlaces < 0) {
        throw new Error("Decimal places must be greater than or equal to 0");
    }

    if(decimalPlaces === 0) {
        return Math.round(amount);
    }

    // Check to ensure we're not 6 orders of magnitude smaller than what we're targeting
    if(Math.abs(amount) < Math.pow(10, -(decimalPlaces + 6))) {
        return 0;
    }

    // Previously we used to use a simple scaling solution like
    // `Math.round(amount * decimalLength) / decimalLength`
    // but unfortunately that has issues with numbers like 8.995, so we're having to use some string arithmetic
    return Number(Math.round(Number(`${amount}e${decimalPlaces}`)) + `e-${decimalPlaces}`);
}

/**
 * Drop trailing zeros if they exist, otherwise round to two decimal places
 */
export function dropTrailing(
    amount: number,
    blankIfZero = false
): string {
    if(!amount && blankIfZero || isNaN(amount) && blankIfZero) {
        return "";
    } else if(isNaN(amount)) {
        amount = 0;
    }

    // Round the amount
    amount = Math.round(amount * 100) / 100;

    let decimalPlaces = 2;

    if(amount % 1 === 0) {
        decimalPlaces = 0;
    }

    return toDecimalPlaces(amount, decimalPlaces);
}

type RoundDirection = "auto" | "up" | "down";

/**
 * Returns value rounded to the closest to value
 */
export function roundTo(
    value: number,
    to: number,
    direction: RoundDirection = "auto"
): number {
    const decimalResolution = getDecimalResolution(to);

    const precision = Math.pow(10, decimalResolution);

    if(direction === "up") {
        return ((to * precision) * Math.ceil(value / to)) / precision;
    } else if(direction === "down") {
        return ((to * precision) * Math.floor(value / to)) / precision;
    }

    return ((to * precision) * Math.round(value / to)) / precision;
}

/**
 * Trim the decimal places off a number that are equal to zero
 * @example
 * // Returns "12.34"
 * trimDecimals(12.3400)
 * @example
 * // Returns "12.345"
 * trimDecimals(12.345)
 * @example
 * // Returns "12.340"
 * trimDecimals(12.34000, 3)
 */
export function trimDecimals(
    value: number | string,
    ideal = currencyDecimalPlaces
): string {
    if(typeof value === "string") {
        if(isNaN(parseFloat(value))) {
            return value;
        }
    } else if(isNaN(value)) {
        return value.toString();
    }

    value = value.toString();

    if(value.split(".").length < 2) {
        return value;
    }

    while(value.charAt(value.length - 1) === "0") {
        value = value.slice(0, -1);
    }

    if(value.split(".")[1].length < ideal) {
        value = toDecimalPlaces(parseFloat(value), ideal).toString();
    }

    return value;
}
